import { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useJsApiLoader, Autocomplete } from "@react-google-maps/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment-timezone";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "components/dialog";
import { Button } from "components/button";
import {
  MapPinIcon,
  CalendarDaysIcon,
  CreditCardIcon,
  BanknotesIcon,
  TicketIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";
import { ToastError } from "utils/toasts";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES } from "utils/googleMaps";

const PAYMENT_METHODS = [
  { value: "cash", label: "Cash", Icon: BanknotesIcon },
  { value: "card", label: "Card", Icon: CreditCardIcon },
  { value: "voucher", label: "Voucher", Icon: TicketIcon },
];

function toZonedDate(utcString, timeZone) {
  if (!utcString) return null;
  const m = moment.tz(utcString, timeZone);
  return new Date(m.year(), m.month(), m.date(), m.hours(), m.minutes(), 0);
}

function fromZonedDate(localDate, timeZone) {
  if (!localDate) return null;
  return moment
    .tz(
      {
        year: localDate.getFullYear(),
        month: localDate.getMonth(),
        date: localDate.getDate(),
        hour: localDate.getHours(),
        minute: localDate.getMinutes(),
      },
      timeZone
    )
    .toISOString();
}

function nowInZone(timeZone) {
  const m = moment.tz(timeZone);
  return new Date(m.year(), m.month(), m.date(), m.hours(), m.minutes(), 0);
}

export default function EditTripDialog({
  open,
  onClose,
  tripDetails,
  tripId,
  onSave,
}) {
  const [isSaving, setIsSaving] = useState(false);
  const [pickupCoords, setPickupCoords] = useState({ lat: null, lng: null });
  const [dropoffCoords, setDropoffCoords] = useState({ lat: null, lng: null });
  const [pickupConfirmed, setPickupConfirmed] = useState("");
  const [dropoffConfirmed, setDropoffConfirmed] = useState("");
  const pickupAutoRef = useRef(null);
  const dropoffAutoRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm();

  const pickupTime = watch("pickupTime");

  useEffect(() => {
    if (open && tripId) {
      fetch(`${process.env.REACT_APP_BASEURL}assigns/tracking/${tripId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      })
        .then((res) => res.json())
        .then((data) => {
          console.log("tracking payload:", data);
        })
        .catch((err) => {
          console.error("tracking fetch error:", err);
        });
    }
  }, [open, tripId]);

  const timeZone = tripDetails?.company?.timeZone || "UTC";

  useEffect(() => {
    if (open && tripDetails) {
      reset({
        pickupAddress: tripDetails.jobOriginAddress || "",
        pickupTime: toZonedDate(tripDetails.scheduleTime, timeZone) || nowInZone(timeZone),
        dropoffAddress: tripDetails.jobDestinationAddress || "",
        dropoffTime: toZonedDate(tripDetails.appointmentTime, timeZone) || nowInZone(timeZone),
        payment: tripDetails.paymentMethod || "cash",
        companyNote: tripDetails.companyNote || "",
      });
      setPickupCoords({
        lat: tripDetails.jobOriginLatitude ?? null,
        lng: tripDetails.jobOriginLongitude ?? null,
      });
      setDropoffCoords({
        lat: tripDetails.jobDestinationLatitude ?? null,
        lng: tripDetails.jobDestinationLongitude ?? null,
      });
      setPickupConfirmed(tripDetails.jobOriginAddress || "");
      setDropoffConfirmed(tripDetails.jobDestinationAddress || "");
    }
  }, [open, tripDetails, reset, timeZone]);

  const onPickupPlaceChanged = () => {
    const place = pickupAutoRef.current?.getPlace();
    if (place?.formatted_address && place.geometry?.location) {
      setValue("pickupAddress", place.formatted_address, { shouldValidate: true });
      setPickupCoords({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      });
      setPickupConfirmed(place.formatted_address);
    }
  };

  const onDropoffPlaceChanged = () => {
    const place = dropoffAutoRef.current?.getPlace();
    if (place?.formatted_address && place.geometry?.location) {
      setValue("dropoffAddress", place.formatted_address, { shouldValidate: true });
      setDropoffCoords({
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      });
      setDropoffConfirmed(place.formatted_address);
    }
  };

  const handleSave = async (data) => {
    setIsSaving(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASEURL}assigns/tracking/${tripId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            _id: tripDetails._id,
            company: tripDetails.company._id,
            jobOriginAddress: data.pickupAddress,
            jobOriginLatitude: pickupCoords.lat,
            jobOriginLongitude: pickupCoords.lng,
            scheduleTime: fromZonedDate(data.pickupTime, timeZone),
            jobDestinationAddress: data.dropoffAddress,
            jobDestinationLatitude: dropoffCoords.lat,
            jobDestinationLongitude: dropoffCoords.lng,
            appointmentTime: fromZonedDate(data.dropoffTime, timeZone),
            paymentMethod: data.payment,
            companyNote: data.companyNote,
          }),
        }
      );
      await response.json();
      onClose();
      onSave();
    } catch {
      ToastError("Failed to update trip.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle className="text-zinc-900 font-semibold text-base">
        Edit Trip
      </DialogTitle>

      <form onSubmit={handleSubmit(handleSave)}>
        <DialogBody className="mt-4 space-y-6">
          <div>
            {/* Pickup */}
            <SectionLabel>Pickup</SectionLabel>
            <div className="space-y-2">
              <Field error={errors.pickupAddress?.message}>
                <InputRow
                  icon={<MapPinIcon className="size-4 text-zinc-400" />}
                  last={true}
                >
                  {isLoaded ? (
                    <Autocomplete
                      onLoad={(ref) => (pickupAutoRef.current = ref)}
                      onPlaceChanged={onPickupPlaceChanged}
                    >
                      <input
                        {...register("pickupAddress", {
                          required: "Required",
                          validate: (value) =>
                            value === pickupConfirmed ||
                            "Select an address from the suggestions",
                        })}
                        placeholder="Pickup address"
                        aria-label="Pickup address"
                        className={inputClass(!!errors.pickupAddress)}
                      />
                    </Autocomplete>
                  ) : (
                    <input
                      {...register("pickupAddress", {
                        required: "Required",
                        validate: (value) =>
                          value === pickupConfirmed ||
                          "Select an address from the suggestions",
                      })}
                      placeholder="Pickup address"
                      aria-label="Pickup address"
                      className={inputClass(!!errors.pickupAddress)}
                    />
                  )}
                </InputRow>
              </Field>
              <Field error={errors.pickupTime?.message}>
                <InputRow
                  icon={<CalendarDaysIcon className="size-4 text-zinc-400" />}
                  last
                >
                  <Controller
                    name="pickupTime"
                    control={control}
                    rules={{ required: "Required" }}
                    render={({ field }) => (
                      <DatePicker
                        selected={field.value}
                        onChange={field.onChange}
                        showTimeSelect
                        timeIntervals={5}
                        dateFormat="MMM d, yyyy h:mm aa"
                        placeholderText="Pickup date & time"
                        className={inputClass(!!errors.pickupTime)}
                        wrapperClassName="w-full"
                      />
                    )}
                  />
                </InputRow>
              </Field>
            </div>
          </div>

          <div>
            {/* Dropoff */}
            <SectionLabel>Dropoff</SectionLabel>
            <div className="space-y-2">
              <Field error={errors.dropoffAddress?.message}>
                <InputRow
                  icon={<MapPinIcon className="size-4 text-zinc-400" />}
                  last={true}
                >
                  {isLoaded ? (
                    <Autocomplete
                      onLoad={(ref) => (dropoffAutoRef.current = ref)}
                      onPlaceChanged={onDropoffPlaceChanged}
                    >
                      <input
                        {...register("dropoffAddress", {
                          required: "Required",
                          validate: (value) =>
                            value === dropoffConfirmed ||
                            "Select an address from the suggestions",
                        })}
                        placeholder="Dropoff address"
                        aria-label="Dropoff address"
                        className={inputClass(!!errors.dropoffAddress)}
                      />
                    </Autocomplete>
                  ) : (
                    <input
                      {...register("dropoffAddress", {
                        required: "Required",
                        validate: (value) =>
                          value === dropoffConfirmed ||
                          "Select an address from the suggestions",
                      })}
                      placeholder="Dropoff address"
                      aria-label="Dropoff address"
                      className={inputClass(!!errors.dropoffAddress)}
                    />
                  )}
                </InputRow>
              </Field>
              <Field error={errors.dropoffTime?.message}>
                <InputRow
                  icon={<CalendarDaysIcon className="size-4 text-zinc-400" />}
                  last
                >
                  <Controller
                    name="dropoffTime"
                    control={control}
                    rules={{ required: "Required" }}
                    render={({ field }) => {
                      const sameDay =
                        field.value &&
                        pickupTime &&
                        field.value.toDateString() === pickupTime.toDateString();
                      return (
                        <DatePicker
                          selected={field.value}
                          onChange={field.onChange}
                          showTimeSelect
                          timeIntervals={5}
                          dateFormat="MMM d, yyyy h:mm aa"
                          placeholderText="Dropoff date & time"
                          minDate={pickupTime || undefined}
                          minTime={sameDay ? pickupTime : new Date(new Date().setHours(0, 0, 0, 0))}
                          maxTime={new Date(new Date().setHours(23, 59, 59, 0))}
                          className={inputClass(!!errors.dropoffTime)}
                          wrapperClassName="w-full"
                        />
                      );
                    }}
                  />
                </InputRow>
              </Field>
            </div>
          </div>

          <div>
            {/* Payment */}
            <SectionLabel>Payment</SectionLabel>
            <div className="flex rounded-xl bg-zinc-100 p-1 gap-1">
              {PAYMENT_METHODS.map(({ value, label, Icon }) => (
                <label
                  key={value}
                  className="relative flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-lg py-2.5 transition-all has-[:checked]:bg-white has-[:checked]:shadow-sm"
                >
                  <input
                    type="radio"
                    value={value}
                    {...register("payment", { required: true })}
                    className="sr-only"
                  />
                  <Icon className="size-4 text-zinc-400 [label:has(:checked)_&]:text-zinc-700" />
                  <span className="text-sm font-medium text-zinc-500 [label:has(:checked)_&]:text-zinc-900">
                    {label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div>
            {/* Notes */}
            <SectionLabel>Notes</SectionLabel>
            <div className="flex gap-3 py-2 px-2 -mx-2 border-b border-zinc-200 focus-within:border-[#293562] transition-colors">
              <span className="shrink-0 pointer-events-none pt-1">
                <DocumentTextIcon className="size-4 text-zinc-400" />
              </span>
              <textarea
                {...register("companyNote")}
                placeholder="Company notes"
                aria-label="Company notes"
                rows={3}
                className={`${inputClass(false)} resize-none`}
              />
            </div>
          </div>
        </DialogBody>

        <DialogActions>
          <Button plain type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="primary" disabled={isSaving}>
            {isSaving ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function SectionLabel({ children, className = "" }) {
  return (
    <p
      className={`text-[0.6875rem] font-semibold uppercase tracking-widest text-zinc-400 mb-1 ${className}`}
    >
      {children}
    </p>
  );
}

/* Border lives on the ROW (full width), not on the input */
function InputRow({ icon, children, last }) {
  return (
    <div
      className={`flex items-center gap-3 py-2 px-2 -mx-2 transition-colors ${last ? "" : "border-b border-zinc-200"}`}
    >
      <span className="shrink-0 pointer-events-none">{icon}</span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function Field({ error, children }) {
  return (
    <div className="border-b border-zinc-200 focus-within:border-[#293562] transition-colors">
      {children}
      {error && <p className="mb-1 pl-7 text-xs text-red-500">{error}</p>}
    </div>
  );
}

function inputClass(hasError) {
  return [
    "w-full border-0 outline-none ring-0 bg-transparent py-1 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:ring-0 focus-visible:outline-none",
    hasError ? "text-red-600 placeholder:text-red-400" : "",
  ].join(" ");
}
